import {
  getAllAnaliseRows,
  getAllCriteriosRows,
  getAllPrazosRows,
  getOrgaoEmailsList,
  valorDetalhesTexto,
} from 'components/molecules/bidding_summary_utils';

import type {
  BidderConsiderationTeamRow,
  BiddingSummaryData,
  DocumentRequirementItem,
} from 'store/modules/bidding_summary_store/types';

import { formatIsoDateBr, formatIsoDateTimeBr } from 'services/format_iso_date_br';

const NAO_INFORMADO = 'Não informado';

export type ReportBlock =
  | { kind: 'rows'; rows: { label: string; value: string }[] }
  | { kind: 'list'; items: string[] }
  | { kind: 'text'; text: string }
  | {
      kind: 'table';
      /** width em % da linha; as colunas devem somar 100. */
      columns: { label: string; width: number; align?: 'left' | 'right' }[];
      rows: string[][];
    };

export interface ReportSection {
  title: string;
  blocks: ReportBlock[];
}

export interface BidderReportQuestion {
  id: number;
  content: string;
}

export interface BidderReportData {
  biddingNumber: string;
  publicBody: string;
  conlicitacaoId: string;
  generatedAt: string;
  sections: ReportSection[];
}

const text = (value?: string | null): string => value?.trim() || NAO_INFORMADO;

/** Só entra no PDF o que tem conteúdo — evita páginas com dezenas de "Não informado". */
const cleanList = (items?: (string | null)[] | null): string[] =>
  (items ?? []).map((item) => (item ?? '').trim()).filter(Boolean);

/**
 * documentos_habilitacao/anexos_declaracoes/atestado_capacidade_tecnica podem vir como string
 * (formato legado) ou objeto `{documento, trecho_edital, confianca}` (formato novo, rastreável).
 * O PDF é texto puro (sem expandir/recolher como na tela), então mostra só o nome do documento,
 * marcando os de baixa confiança com o mesmo rótulo usado no badge da UI.
 */
const documentRequirementLabel = (
  item: string | DocumentRequirementItem | null | undefined,
): string => {
  if (typeof item === 'string') return item.trim();
  const documento = item?.documento?.trim();
  if (!documento) return '';
  return item?.confianca === 'baixa' ? `${documento} (a confirmar)` : documento;
};

const cleanDocumentRequirementList = (
  items?: (string | DocumentRequirementItem | null)[] | null,
): string[] => (items ?? []).map(documentRequirementLabel).filter(Boolean);

const rowsBlock = (rows: { label: string; value: string }[]): ReportBlock[] =>
  rows.length ? [{ kind: 'rows', rows }] : [];

/**
 * Os helpers do resumo devolvem `NAO_INFORMADO` como fallback de todo campo vazio.
 * No PDF isso viraria página atrás de página de "Não informado", então some.
 */
const filledRowsBlock = (rows: { label: string; value: string }[]): ReportBlock[] =>
  rowsBlock(rows.filter((row) => row.value !== NAO_INFORMADO));

const listBlock = (items: string[]): ReportBlock[] =>
  items.length ? [{ kind: 'list', items }] : [];

const section = (title: string, blocks: ReportBlock[]): ReportSection[] =>
  blocks.length ? [{ title, blocks }] : [];

const buildAnalysisSections = (summary: BiddingSummaryData): ReportSection[] => {
  const dataSessao = formatIsoDateBr(summary.data_sessao_publica);
  const emails = getOrgaoEmailsList(summary);

  const itens = summary.resumo_itens_licitados;
  const itensRows = [
    ...(itens?.total_itens ? [{ label: 'Total de itens', value: String(itens.total_itens) }] : []),
    ...(cleanList(itens?.categorias_principais).length
      ? [
          {
            label: 'Categorias principais',
            value: cleanList(itens.categorias_principais).join(', '),
          },
        ]
      : []),
    ...(itens?.descricao_geral?.trim()
      ? [{ label: 'Descrição geral', value: itens.descricao_geral.trim() }]
      : []),
    ...(itens?.observacoes?.trim()
      ? [{ label: 'Observações', value: itens.observacoes.trim() }]
      : []),
  ];

  return [
    ...section('Visão geral', [
      {
        kind: 'rows',
        rows: [
          { label: 'Valor estimado', value: valorDetalhesTexto(summary) || NAO_INFORMADO },
          {
            label: 'Modalidade',
            value: text(summary.tipo_contratacao || summary.modo_disputa),
          },
          { label: 'Data da sessão', value: dataSessao || NAO_INFORMADO },
          { label: 'Horário da sessão', value: text(summary.horario_sessao_publica) },
        ],
      },
    ]),
    ...section('Identificação da licitação', [
      {
        kind: 'rows',
        rows: [
          { label: 'Objeto', value: text(summary.objeto) },
          { label: 'Número da licitação', value: text(summary.numero_licitacao) },
          { label: 'UASG', value: text(summary.uasg) },
          { label: 'Portal de realização', value: text(summary.portal_realizacao) },
          { label: 'Contratação', value: text(summary.tipo_contratacao) },
          { label: 'Critério de julgamento', value: text(summary.criterio_de_julgamento) },
        ],
      },
    ]),
    ...section('Órgão responsável', [
      {
        kind: 'rows',
        rows: [
          { label: 'Órgão', value: text(summary.orgao_responsavel) },
          { label: 'Endereço', value: text(summary.orgao_endereco) },
          { label: 'E-mails', value: emails.length ? emails.join(', ') : NAO_INFORMADO },
          { label: 'Telefone', value: text(summary.telefone_orgao) },
        ],
      },
    ]),
    ...section('Entrega e execução', [
      {
        kind: 'rows',
        rows: [
          { label: 'Prazo de entrega', value: text(summary.prazo_de_entrega) },
          { label: 'Data de entrega/execução', value: text(summary.data_entrega_execucao) },
          { label: 'Local de entrega/execução', value: text(summary.local_entrega_execucao) },
        ],
      },
    ]),
    ...section('Prazos importantes', filledRowsBlock(getAllPrazosRows(summary))),
    ...section('Critérios da proposta e julgamento', filledRowsBlock(getAllCriteriosRows(summary))),
    ...section('Resumo dos itens licitados', rowsBlock(itensRows)),
    ...section(
      'Documentos de habilitação',
      listBlock(cleanDocumentRequirementList(summary.documentos_habilitacao)),
    ),
    ...section(
      'Atestado de capacidade técnica',
      listBlock(cleanDocumentRequirementList(summary.atestado_capacidade_tecnica)),
    ),
    ...section('Legislação', listBlock(cleanList(summary.legislacao))),
    ...section(
      'Anexos e declarações',
      listBlock(cleanDocumentRequirementList(summary.anexos_declaracoes)),
    ),
    ...section(
      'Condições de pagamento',
      summary.condicoes_pagamento?.trim()
        ? [{ kind: 'text', text: summary.condicoes_pagamento.trim() }]
        : [],
    ),
    ...section('Penalidades e multas', listBlock(cleanList(summary.penalidades_multas))),
    ...section('Análise crítica', filledRowsBlock(getAllAnaliseRows(summary))),
    ...section('Riscos identificados', listBlock(cleanList(summary.riscos_identificados))),
    ...section(
      'Oportunidades identificadas',
      listBlock(cleanList(summary.oportunidades_identificadas)),
    ),
    ...section(
      'Outras informações relevantes',
      listBlock(cleanList(summary.outras_informacoes_relevantes)),
    ),
  ];
};

const buildConsiderationSections = (
  consideration: string,
  team: BidderConsiderationTeamRow[],
): ReportSection[] => {
  const blocks: ReportBlock[] = [];

  if (consideration.trim()) {
    blocks.push({ kind: 'text', text: consideration.trim() });
  }

  const teamRows = team
    .filter((row) => row.consideration?.trim())
    .map((row) => ({
      label: `${row.contact_name || 'Equipe'} — ${
        formatIsoDateTimeBr(row.updated_at) || ''
      }`.trim(),
      value: row.consideration.trim(),
    }));

  if (teamRows.length) blocks.push({ kind: 'rows', rows: teamRows });

  return section('Análise e considerações do licitante', blocks);
};

const buildQuestionSections = (questions: BidderReportQuestion[]): ReportSection[] =>
  section(
    'Pergunte ao Edital',
    listBlock(questions.map((question) => question.content.trim()).filter(Boolean)),
  );

interface BuildBidderReportDataParams {
  summary: BiddingSummaryData | null;
  biddingNumber: string;
  publicBody: string;
  conlicitacaoId: string;
  withAnalysis: boolean;
  bidderConsideration: string;
  bidderConsiderationTeam: BidderConsiderationTeamRow[];
  withConsideration: boolean;
  questions: BidderReportQuestion[];
}

export const buildBidderReportData = ({
  summary,
  biddingNumber,
  publicBody,
  conlicitacaoId,
  withAnalysis,
  bidderConsideration,
  bidderConsiderationTeam,
  withConsideration,
  questions,
}: BuildBidderReportDataParams): BidderReportData => ({
  biddingNumber,
  publicBody,
  conlicitacaoId,
  generatedAt: formatIsoDateTimeBr(new Date().toISOString()),
  sections: [
    ...(withAnalysis && summary ? buildAnalysisSections(summary) : []),
    ...(withConsideration
      ? buildConsiderationSections(bidderConsideration, bidderConsiderationTeam)
      : []),
    ...(questions.length ? buildQuestionSections(questions) : []),
  ],
});
